import { ConnectedPosition, CdkOverlayOrigin, CdkConnectedOverlay } from '@angular/cdk/overlay';
import {
  Component,
  ViewEncapsulation,
  forwardRef,
  ChangeDetectorRef,
  OnDestroy,
  ElementRef,
  inject,
  input,
  output,
  effect,
  signal,
  viewChild,
  computed,
  Injector,
  AfterContentInit,
  DestroyRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { Observable, Subscription, Subject, debounceTime, distinctUntilChanged } from 'rxjs';

/**
 * Custom select component with support for both synchronous and asynchronous options.
 *
 * Features:
 * - ngModel support via ControlValueAccessor
 * - Async options support via Observable<any[]>
 * - Typeahead/search functionality with debouncing
 * - Loading state management
 * - Custom binding for value and label properties
 * - Keyboard: Enter, Space or the arrow keys open the list and move into it;
 *   arrows, Home/End and typing move the active option; Enter or Space picks;
 *   Escape closes and returns focus to the field
 *
 * Usage examples:
 *
 * Synchronous options:
 * <app-select
 *   [(ngModel)]="selectedValue"
 *   [options]="staticOptions"
 *   bindValue="id"
 *   bindLabel="name">
 * </app-select>
 *
 * Asynchronous options:
 * <app-select
 *   [(ngModel)]="selectedValue"
 *   [options]="options$"
 *   bindValue="id"
 *   bindLabel="name">
 * </app-select>
 *
 * Or using asyncOptions input:
 * <app-select
 *   [(ngModel)]="selectedValue"
 *   [asyncOptions]="options$"
 *   bindValue="id"
 *   bindLabel="name">
 * </app-select>
 *
 * With typeahead/search functionality:
 * <app-select
 *   [(ngModel)]="selectedValue"
 *   [options]="options"
 *   [searchable]="true"
 *   [searchPlaceholder]="'Search options…'"
 *   [minSearchLength]="2"
 *   [debounceTime]="300"
 *   bindValue="id"
 *   bindLabel="name"
 *   (search)="onSearch($event)">
 * </app-select>
 */
@Component({
    selector: 'app-select',
    templateUrl: './select.component.html',
    styleUrls: ['./select.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectComponent),
            multi: true
        }
    ],
    imports: [CdkOverlayOrigin, CdkConnectedOverlay]
})
export class SelectComponent implements AfterContentInit, OnDestroy, ControlValueAccessor {
  private static nextId = 0;
  private cdr = inject(ChangeDetectorRef);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  // Signal inputs
  options = input<unknown[] | Observable<unknown[]> | null>([]);
  asyncOptions = input<Observable<unknown[]> | null>(null);
  disabled = input(false);
  multi = input(false);
  bindValue = input<string | undefined>(undefined);
  bindLabel = input<string | undefined>(undefined);
  loading = input(false);
  placeholder = input('Select an option');
  searchable = input(false);
  searchPlaceholder = input('Search…');
  minSearchLength = input(0);
  debounceTimeMs = input(300, { alias: 'debounceTime' });
  clearable = input(false);
  /** Accessible name of the field; the trigger announces it with the current choice. */
  ariaLabel = input<string | null>(null);
  /**
   * Marks the field invalid. Left null, a bound form control decides: invalid
   * once it has been touched.
   */
  invalid = input<boolean | null>(null);
  /**
   * Max height of the open list, as a CSS value ('none' shows every option).
   * Left null, the list keeps its default 200px and scrolls beyond it.
   */
  listMaxHeight = input<string | null>(null);

  // Signal outputs
  change = output<unknown>();
  search = output<string>();

  // Signal view children
  originElement = viewChild<ElementRef>('trigger');
  private readonly triggerButton = viewChild<ElementRef<HTMLButtonElement>>('triggerButton');
  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  private readonly listbox = viewChild<ElementRef<HTMLElement>>('listbox');

  private readonly instanceId = `if-select-${SelectComponent.nextId++}`;
  readonly listboxId = `${this.instanceId}-listbox`;

  /** Invalid and touched state of the bound form control, refreshed on its events. */
  private readonly controlInvalid = signal(false);
  readonly isInvalid = computed(() => !this.isDisabled() && (this.invalid() ?? this.controlInvalid()));

  /** Internal disabled state that can be toggled by CVA setDisabledState */
  _disabledByCva = signal(false);

  /** Effective disabled state */
  isDisabled = computed(() => this.disabled() || this._disabledByCva());

  /** Internal loading state that can be toggled by async subscriptions */
  _loadingInternal = signal(false);

  /** Effective loading state */
  isLoading = computed(() => this.loading() || this._loadingInternal());

  placeholderVisible = false;

  private _selected: unknown = null;
  private _isOpen: boolean = false;
  private currentValue: unknown = null;
  private optionsSubscription: Subscription | null = null;
  private _options: unknown[] = [];
  private _filteredOptions: unknown[] = [];
  private searchSubject = new Subject<string>();
  private searchSubscription: Subscription | null = null;
  searchTerm = '';
  /** The active option (keyboard position or pointer), an index into currentOptions. */
  highlightedIndex = -1;
  /** The active option was reached with the keyboard, so it shows the focus ring. */
  keyboardActive = false;
  searchValue = '';
  private typeahead = '';
  private typeaheadTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Set up search subscription with debouncing
    this.searchSubscription = this.searchSubject
      .pipe(debounceTime(this.debounceTimeMs()), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.performSearch(searchTerm);
      });
  }

  // After the node's own directives ran: formControlName links its control in ngOnChanges.
  ngAfterContentInit(): void {
    const control = this.injector.get(NgControl, null, { self: true, optional: true })?.control;
    if (!control) return;
    const sync = (): void => this.controlInvalid.set(control.invalid && control.touched);
    sync();
    const sub = control.events.subscribe(() => {
      sync();
      this.cdr.markForCheck();
    });
    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  /** React to options input changes */
  private optionsEffect = effect(() => {
    const opts = this.options();
    this.handleOptionsChangeInternal(opts);
  });

  /** React to asyncOptions input changes */
  private asyncOptionsEffect = effect(() => {
    const asyncOpts = this.asyncOptions();
    if (asyncOpts) {
      this.subscribeToOptions(asyncOpts);
    }
  });

  /** React to disabled input changes - close overlay when disabled */
  private disabledEffect = effect(() => {
    if (this.isDisabled() && this._isOpen) {
      this._isOpen = false;
      this.cdr.detectChanges();
    }
  });

  get originWidth(): number {
    const el = this.originElement();
    return el?.nativeElement?.offsetWidth || 0;
  }

  get selected(): unknown {
    return this._selected;
  }

  get currentOptions(): unknown[] {
    return this.searchable() ? this._filteredOptions : this._options;
  }

  get isSearching(): boolean {
    return this.searchable() && this.searchTerm.length >= this.minSearchLength();
  }

  set selected(value: unknown) {
    this._selected = value;
    this.cdr.detectChanges();
  }

  get isOpen(): boolean {
    return this._isOpen;
  }

  set isOpen(value: boolean) {
    this._isOpen = value;
    this.cdr.detectChanges();
  }

  get selectedLabel(): string {
    const bLabel = this.bindLabel();
    if (this.multi()) {
      if (!this.selected || (this.selected as unknown[]).length === 0) {
        this.placeholderVisible = true;
        return this.placeholder();
      }
      this.placeholderVisible = false;
      if ((this.selected as unknown[]).length === 1) {
        const first = (this.selected as Record<string, unknown>[])[0];
        return bLabel ? String(first[bLabel]) : String(first);
      }
      return `${(this.selected as unknown[]).length} items selected`;
    }

    const selected = this.selected;
    if (!selected) {
      this.placeholderVisible = true;
      return this.placeholder();
    }
    this.placeholderVisible = false;
    return bLabel ? String((selected as Record<string, unknown>)[bLabel]) : String(selected);
  }

  // ControlValueAccessor implementation
  private onChange = (_value: unknown) => {};
  private onTouched = () => {};

  writeValue(value: unknown): void {
    this.currentValue = value;
    const bValue = this.bindValue();

    if (value == null) {
      this.selected = this.multi() ? [] : null;
      return;
    }

    if (bValue) {
      if (this.multi()) {
        if (Array.isArray(value)) {
          this.selected = this._options.filter((opt: unknown) => value.includes((opt as Record<string, unknown>)[bValue]));
        } else {
          this.selected = [];
        }
      } else {
        const option = this._options.find((opt: unknown) => (opt as Record<string, unknown>)[bValue] === value);
        this.selected = option || null;
      }
    } else {
      this.selected = value;
    }
  }

  registerOnChange(fn: (_value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledByCva.set(isDisabled);
  }

  isSelected(option: unknown): boolean {
    if (this.multi()) {
      return !!(this.selected && (this.selected as unknown[]).some((item: unknown) => item === option));
    }
    const selected = this.selected;
    if (!selected) return false;
    if (this.bindValue()) {
      return selected === option;
    }
    return option === selected;
  }

  overlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' }
  ];

  toggleDropdown(event?: MouseEvent): void {
    // don't open/close if disabled
    if (this.isDisabled()) return;
    if (this.isOpen) {
      this.closeDropdown(true);
    } else {
      this.openDropdown();
      // A click with no pointer behind it came from Enter or Space.
      if (event && event.detail === 0) this.keyboardActive = true;
    }
  }

  /** Opens the list with the chosen option (else the first) active. */
  openDropdown(position: 'selected' | 'first' | 'last' = 'selected'): void {
    if (this.isDisabled() || this.isOpen) return;
    const options = this.currentOptions;
    const selectedIndex = options.findIndex((o) => this.isSelected(o));
    this.highlightedIndex =
      position === 'last' ? options.length - 1 : position === 'first' || selectedIndex < 0 ? (options.length ? 0 : -1) : selectedIndex;
    this.keyboardActive = false;
    this.isOpen = true;
    this.onDropdownOpen();
  }

  /** Closes the list; `restoreFocus` puts focus back on the field. */
  closeDropdown(restoreFocus = false): void {
    if (!this._isOpen) return;
    this.isOpen = false;
    this.keyboardActive = false;
    this.highlightedIndex = -1;
    this.clearSearch();
    this.onTouched();
    if (restoreFocus) this.triggerButton()?.nativeElement.focus();
  }

  /** The list is in the DOM: move focus into it (or its search field). */
  onOverlayAttached(): void {
    setTimeout(() => {
      if (!this._isOpen) return;
      if (this.searchable()) this.searchInput()?.nativeElement.focus();
      else this.listbox()?.nativeElement.focus({ preventScroll: true });
      this.scrollActiveIntoView();
    });
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    if (this.isDisabled()) return;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        this.openDropdown(this.selected ? 'selected' : event.key === 'ArrowUp' ? 'last' : 'first');
        this.keyboardActive = true;
        break;
    }
  }

  onTriggerBlur(): void {
    if (!this._isOpen) this.onTouched();
  }

  /** Keys inside the open list (non-searchable select; the list has focus). */
  onListboxKeydown(event: KeyboardEvent): void {
    if (this.searchable()) return;
    const options = this.currentOptions;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveActive(this.highlightedIndex < 0 ? 0 : Math.min(options.length - 1, this.highlightedIndex + 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActive(this.highlightedIndex < 0 ? options.length - 1 : Math.max(0, this.highlightedIndex - 1));
        break;
      case 'Home':
        event.preventDefault();
        this.moveActive(0);
        break;
      case 'End':
        event.preventDefault();
        this.moveActive(options.length - 1);
        break;
      case 'PageDown':
        event.preventDefault();
        this.moveActive(Math.min(options.length - 1, Math.max(0, this.highlightedIndex) + 10));
        break;
      case 'PageUp':
        event.preventDefault();
        this.moveActive(Math.max(0, this.highlightedIndex - 10));
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (event.key === ' ' && this.typeahead) {
          this.typeaheadSearch(' ');
          break;
        }
        this.selectHighlighted();
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.closeDropdown(true);
        break;
      case 'Tab':
        this.closeDropdown(true);
        break;
      default:
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          this.typeaheadSearch(event.key);
        }
    }
  }

  /** The pointer moved over an option: it becomes active without the keyboard ring. */
  onOptionPointer(index: number): void {
    if (this.highlightedIndex === index && !this.keyboardActive) return;
    this.highlightedIndex = index;
    this.keyboardActive = false;
    this.cdr.detectChanges();
  }

  optionId(index: number): string {
    return `${this.instanceId}-option-${index}`;
  }

  optionLabel(option: unknown): string {
    const bLabel = this.bindLabel();
    return bLabel ? String((option as Record<string, unknown>)[bLabel]) : String(option);
  }

  get activeDescendant(): string | null {
    return this._isOpen && this.highlightedIndex >= 0 && this.highlightedIndex < this.currentOptions.length
      ? this.optionId(this.highlightedIndex)
      : null;
  }

  /** The search field's name: what it searches. */
  get searchAriaLabel(): string {
    const name = this.ariaLabel();
    return name ? `Search ${name.toLowerCase()}` : 'Search options';
  }

  private moveActive(index: number): void {
    if (index < 0 || index >= this.currentOptions.length) return;
    this.highlightedIndex = index;
    this.keyboardActive = true;
    this.cdr.detectChanges();
    this.scrollActiveIntoView();
  }

  private scrollActiveIntoView(): void {
    const panel = this.listbox()?.nativeElement;
    const id = this.activeDescendant;
    if (!panel || !id) return;
    panel.querySelector<HTMLElement>(`#${id}`)?.scrollIntoView?.({ block: 'nearest' });
  }

  /** Jumps to the next option whose label starts with what was typed. */
  private typeaheadSearch(char: string): void {
    if (this.typeaheadTimer) clearTimeout(this.typeaheadTimer);
    this.typeahead += char.toLowerCase();
    this.typeaheadTimer = setTimeout(() => (this.typeahead = ''), 600);
    const options = this.currentOptions;
    const start = this.typeahead.length === 1 ? this.highlightedIndex + 1 : Math.max(0, this.highlightedIndex);
    for (let k = 0; k < options.length; k++) {
      const i = (start + k) % options.length;
      if (this.optionLabel(options[i]).toLowerCase().startsWith(this.typeahead)) {
        this.moveActive(i);
        return;
      }
    }
  }

  selectOption(option: unknown): void {
    if (this.isDisabled()) return;
    const bValue = this.bindValue();

    if (this.multi()) {
      const selectedItems = (this.selected as unknown[]) || [];
      const index = selectedItems.findIndex((item: unknown) => item === option);
      if (index > -1) {
        selectedItems.splice(index, 1);
      } else {
        selectedItems.push(option);
      }
      this.selected = [...selectedItems];
      const values = (this.selected as unknown[]).map((item: unknown) => (bValue ? (item as Record<string, unknown>)[bValue] : item));
      this.currentValue = values;
      this.onChange(values);
      this.change.emit(values);
    } else {
      const selectedOption = bValue ? option : option;
      const value = bValue ? (option as Record<string, unknown>)[bValue] : option;

      this.selected = selectedOption;
      // Keep the CVA's own copy in step: a later options change re-runs
      // updateSelectedFromCurrentValue, which would otherwise resolve a stale
      // currentValue and silently clear what was just picked.
      this.currentValue = value;
      this.onChange(value);
      this.onTouched();
      this.change.emit(value);
      this.closeDropdown(true);
    }

    if (this.searchable()) {
      this.clearSearch();
    }
  }

  clearSelection(event: Event): void {
    event.stopPropagation();
    if (this.multi()) {
      this.selected = [];
      this.currentValue = [];
      this.onChange([]);
      this.change.emit([]);
    } else if (this.clearable()) {
      this.selected = null;
      this.currentValue = null;
      this.onChange(null);
      this.change.emit(null);
    }
  }

  ngOnDestroy(): void {
    if (this.typeaheadTimer) clearTimeout(this.typeaheadTimer);
    if (this.optionsSubscription) {
      this.optionsSubscription.unsubscribe();
    }
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  private handleOptionsChangeInternal(opts: unknown[] | Observable<unknown[]> | null): void {
    if (Array.isArray(opts)) {
      this._options = opts;
      this._filteredOptions = [...this._options];
      this.updateSelectedFromCurrentValue();
    } else if (opts instanceof Observable) {
      this.subscribeToOptions(opts);
    }
  }

  private subscribeToOptions(observable: Observable<unknown[]>): void {
    // Unsubscribe from previous subscription
    if (this.optionsSubscription) {
      this.optionsSubscription.unsubscribe();
    }

    this._loadingInternal.set(true);
    this.optionsSubscription = observable.subscribe({
      next: (options) => {
        this._options = options;
        this._filteredOptions = [...this._options];
        this._loadingInternal.set(false);
        this.updateSelectedFromCurrentValue();
        this.cdr.detectChanges();
      },
      error: () => {
        this._loadingInternal.set(false);
        this._options = [];
        this._filteredOptions = [];
        this.cdr.detectChanges();
      }
    });
  }

  private updateSelectedFromCurrentValue(): void {
    const bValue = this.bindValue();
    if (this.currentValue != null && bValue && this._options.length > 0) {
      if (this.multi()) {
        if (Array.isArray(this.currentValue)) {
          this.selected = this._options.filter((opt) => (this.currentValue as unknown[]).includes((opt as Record<string, unknown>)[bValue]));
        }
      } else {
        const option = this._options.find((opt: unknown) => (opt as Record<string, unknown>)[bValue] === this.currentValue);
        this.selected = option || null;
      }
    }
  }

  // Search methods
  onSearchInput(event: Event): void {
    this.searchValue = (event.target as HTMLInputElement).value;
    this.onSearchValueChange();
  }

  clearSearchAndFocus(): void {
    this.clearSearch();
    this.searchInput()?.nativeElement.focus();
  }

  onSearchValueChange(): void {
    this.searchTerm = this.searchValue;
    this.highlightedIndex = -1;

    if (this.searchTerm.length >= this.minSearchLength()) {
      this.searchSubject.next(this.searchTerm);
    } else {
      this._filteredOptions = [...this._options];
      this.cdr.detectChanges();
    }
  }

  onSearchKeyDown(event: KeyboardEvent): void {
    if (!this.searchable()) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightNext();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightPrevious();
        break;
      case 'Enter':
        event.preventDefault();
        this.selectHighlighted();
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.closeDropdown(true);
        break;
      case 'Tab':
        this.closeDropdown(true);
        break;
    }
  }

  private performSearch(searchTerm: string): void {
    if (!this.searchable()) return;
    const bLabel = this.bindLabel();

    this.search.emit(searchTerm);

    if (searchTerm.length < this.minSearchLength()) {
      this._filteredOptions = [...this._options];
    } else {
      this._filteredOptions = this._options.filter((option) => {
        const searchText = bLabel ? String((option as Record<string, unknown>)[bLabel]) : String(option);
        return searchText.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    this.highlightedIndex = -1;
    this.cdr.detectChanges();
  }

  private highlightNext(): void {
    if (this._filteredOptions.length === 0) return;
    this.highlightedIndex = (this.highlightedIndex + 1) % this._filteredOptions.length;
    this.keyboardActive = true;
    this.cdr.detectChanges();
    this.scrollActiveIntoView();
  }

  private highlightPrevious(): void {
    if (this._filteredOptions.length === 0) return;
    this.highlightedIndex = this.highlightedIndex <= 0 ? this._filteredOptions.length - 1 : this.highlightedIndex - 1;
    this.keyboardActive = true;
    this.cdr.detectChanges();
    this.scrollActiveIntoView();
  }

  private selectHighlighted(): void {
    const options = this.currentOptions;
    if (this.highlightedIndex >= 0 && this.highlightedIndex < options.length) {
      this.selectOption(options[this.highlightedIndex]);
    }
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.searchValue = '';
    this.highlightedIndex = -1;
    this._filteredOptions = [...this._options];
    this.cdr.detectChanges();
  }

  isHighlighted(option: unknown): boolean {
    const index = this.currentOptions.findIndex((opt) => opt === option);
    return index >= 0 && index === this.highlightedIndex;
  }

  /** The trigger's name: the field with its current choice. */
  get triggerAriaLabel(): string | null {
    const name = this.ariaLabel();
    return name ? `${name}: ${this.selectedLabel}` : null;
  }

  /** Shows the full choice as a tooltip only while the label is cut off. */
  syncTruncationTitle(event: Event): void {
    const trigger = event.currentTarget as HTMLElement | null;
    const label = trigger?.querySelector<HTMLElement>('.select-label');
    if (!trigger || !label) return;
    if (label.scrollWidth > label.clientWidth) {
      trigger.setAttribute('title', this.selectedLabel);
    } else {
      trigger.removeAttribute('title');
    }
  }

  /** Whether the clear button should be shown */
  get showClearButton(): boolean {
    if (this.isDisabled()) return false;
    if (this.multi()) {
      return !!this.selected && (this.selected as unknown[]).length > 0;
    }
    return this.clearable() && !!this.selected;
  }

  /** Focus moves into the list once the overlay has attached (onOverlayAttached). */
  onDropdownOpen(): void {
    this.cdr.detectChanges();
  }
}
